import { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

interface BudgetItem {
  id: string;
  category: string;
  subcategory: string | null;
  description: string;
  allocated_amount: number;
  spent_amount: number;
  fiscal_year: number | null;
  status: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  budgetItem: BudgetItem | null;
  tournamentId: string;
  onSuccess: () => void;
}

interface FormState {
  category: string;
  subcategory: string;
  description: string;
  allocated_amount: string;
  fiscal_year: string;
  status: string;
}

const emptyForm = (): FormState => ({
  category: '',
  subcategory: '',
  description: '',
  allocated_amount: '0',
  fiscal_year: String(new Date().getFullYear()),
  status: 'active',
});

export default function MobileBudgetItemSheet({ isOpen, onClose, budgetItem, tournamentId, onSuccess }: Props) {
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setError('');
      if (budgetItem) {
        setForm({
          category: budgetItem.category,
          subcategory: budgetItem.subcategory || '',
          description: budgetItem.description,
          allocated_amount: String(budgetItem.allocated_amount),
          fiscal_year: String(budgetItem.fiscal_year || new Date().getFullYear()),
          status: budgetItem.status,
        });
      } else {
        setForm(emptyForm());
      }
    }
  }, [isOpen, budgetItem]);

  const f = (key: keyof FormState, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.category) { setError('Category is required'); return; }
    if (!form.description.trim()) { setError('Description is required'); return; }
    const amount = parseFloat(form.allocated_amount);
    if (isNaN(amount) || amount < 0) { setError('Allocated amount must be 0 or greater'); return; }

    try {
      setSaving(true);

      const payload = {
        tournament_id: tournamentId,
        category: form.category,
        subcategory: form.subcategory.trim() || null,
        description: form.description.trim(),
        allocated_amount: amount,
        fiscal_year: parseInt(form.fiscal_year) || new Date().getFullYear(),
        status: form.status as 'active' | 'closed' | 'archived',
      };

      if (budgetItem) {
        const { error: err } = await supabase.from('budget_items').update(payload).eq('id', budgetItem.id);
        if (err) throw err;
        toast.success('Budget item updated');
      } else {
        const { error: err } = await supabase.from('budget_items').insert([payload]);
        if (err) throw err;
        toast.success('Budget item added');
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save budget item');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const field = 'w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50';
  const label = 'block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide';

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-t-3xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-golf-gray">{budgetItem ? 'Edit Budget Item' : 'Add Budget Item'}</h2>
            <p className="text-xs text-gray-400 mt-0.5">Fill in the budget item details</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 active:bg-gray-200 transition-colors">
            <XMarkIcon className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSave} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">{error}</div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Category *</label>
              <select value={form.category} onChange={e => f('category', e.target.value)} className={field}>
                <option value="">Select</option>
                <option value="venue">Venue & Facilities</option>
                <option value="prize">Prize Money</option>
                <option value="marketing">Marketing & PR</option>
                <option value="operations">Staff & Operations</option>
                <option value="equipment">Equipment</option>
                <option value="travel">Travel & Accommodation</option>
                <option value="catering">Catering & Hospitality</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className={label}>Subcategory</label>
              <input
                type="text"
                value={form.subcategory}
                onChange={e => f('subcategory', e.target.value)}
                placeholder="Optional"
                className={field}
              />
            </div>
          </div>

          <div>
            <label className={label}>Description *</label>
            <input
              type="text"
              value={form.description}
              onChange={e => f('description', e.target.value)}
              placeholder="Brief description"
              required
              className={field}
            />
          </div>

          <div>
            <label className={label}>Allocated Amount (₦) *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.allocated_amount}
              onChange={e => f('allocated_amount', e.target.value)}
              className={field}
            />
          </div>

          {budgetItem && (
            <div className="bg-gray-50 rounded-xl px-4 py-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Spent Amount</p>
              <p className="text-sm font-bold text-golf-gray">
                ₦{budgetItem.spent_amount.toLocaleString()}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">Auto-calculated from transactions</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Fiscal Year</label>
              <input
                type="number"
                value={form.fiscal_year}
                onChange={e => f('fiscal_year', e.target.value)}
                className={field}
              />
            </div>
            <div>
              <label className={label}>Status *</label>
              <select value={form.status} onChange={e => f('status', e.target.value)} className={field}>
                <option value="active">Active</option>
                <option value="closed">Closed</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          <div className="pb-2" />
        </form>

        <div className="flex gap-3 px-5 py-4 border-t border-gray-100 flex-shrink-0">
          <button type="button" onClick={onClose} className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 bg-white active:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button type="submit" onClick={handleSave} disabled={saving} className="flex-1 py-3 rounded-xl bg-golf-orange text-white text-sm font-semibold active:bg-golf-orange/90 disabled:opacity-60 transition-colors">
            {saving ? 'Saving...' : budgetItem ? 'Save Changes' : 'Add Item'}
          </button>
        </div>
      </div>
    </div>
  );
}
